import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Home, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate(next || "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async () => {
    if (!email) { toast.error("Enter your email first"); return; }
    try {
      await import("@/lib/api").then(({ default: api }) =>
        api.post("/auth/forgot-password", { email, origin_url: window.location.origin })
      );
      toast.success("If that email exists, a reset link is on its way.");
    } catch {
      toast.success("If that email exists, a reset link is on its way.");
    }
  };

  return (
    <div className="min-h-screen bg-bone flex flex-col items-center justify-center px-5 py-10">
      <Link to="/" className="flex items-center gap-2 mb-8">
        <div className="h-9 w-9 rounded-lg bg-forest flex items-center justify-center">
          <Home className="h-5 w-5 text-white" />
        </div>
        <span className="font-heading font-semibold text-lg text-ink">LocalLease</span>
      </Link>

      <div className="w-full max-w-md bg-white border border-line rounded-2xl shadow-sm p-8">
        <h1 className="text-2xl font-heading font-semibold text-ink">Welcome back</h1>
        <p className="text-sm text-mute mt-1">Log in to manage your rentals.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-white border-line" placeholder="you@example.com" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-white border-line" placeholder="••••••••" />
          </div>
          {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end -mt-1">
            <button type="button" data-testid="forgot-password" onClick={forgotPassword} className="text-xs text-mute hover:text-forest">Forgot password?</button>
          </div>
          <button data-testid="login-submit" disabled={loading} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Log in
          </button>
        </form>

        <p className="text-sm text-mute text-center mt-6">
          No account? <Link to={`/register${next ? `?next=${encodeURIComponent(next)}` : ""}`} className="text-forest font-medium hover:underline">Sign up</Link>
        </p>
      </div>
    </div>
  );
}
